import React from 'react'
import styled from 'styled-components'
import { ModalType } from '@app/types'
import { Icon, IconColor, IconTypes, Modal } from '@app/ui'
import {
  resetChannelFilters,
  updateChannelFilters,
} from '../state'
import { useActiveFilterCount, useFilterState, useSportsData, useSportToggle } from '../hooks'
import { TIP_TYPE_OPTIONS, ANALYSIS_OPTIONS } from '../constants'
import { FilterData, FeedFiltersModalProps } from '../types'
import { FilterSection, ToggleGroup, DateGrid } from './FilterSection'
import { SportsGrid } from './SportsGrid'
import { useTranslation } from 'react-i18next'

export const FeedFiltersModal: React.FC<FeedFiltersModalProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const { t } = useTranslation()
  const {
    selectedTipType,
    selectedAnalysis,
    selectedDate,
    setSelectedDate,
    selectedSports,
    setSelectedSports,
    onSelectAnalysis,
    onSelectTipType
  } = useFilterState()
  const filterCount = useActiveFilterCount()

  const {
    sportsOptions,
    sportsNmarketLoading,
    sportsNmarketError
  } = useSportsData()

  const { handleSportToggle } = useSportToggle(selectedSports, setSelectedSports)

  const handleReset = () => {
    resetChannelFilters()
  }

  const handleSubmit = () => {
    const filterData: FilterData = {
      tipType: selectedTipType,
      analysis: selectedAnalysis,
      date: selectedDate,
      sports: selectedSports
    }

    updateChannelFilters(filterData)

    if (onSubmit) {
      onSubmit(filterData)
    }
  }

  return (
    <StyledModal
      open={open}
      type={ModalType.bottomSheet}
      onClose={onClose}
    >
      <ModalContent>
        <TopModalContent>
          <Title>{t('feed.filters')}</Title>
          <CloseButton onClick={onClose}>
            <Icon type={IconTypes.closeMenuIcon} color={IconColor.white} height={25} width={25} />
          </CloseButton>
        </TopModalContent>

        <FilterContent>
          <FilterSection title={t('feed.tipType')} direction="row">
            <ToggleGroup
              options={TIP_TYPE_OPTIONS}
              selected={selectedTipType}
              onSelect={onSelectTipType}
            />
          </FilterSection>

          <FilterSection title={t('common.analysis')} direction="row">
            <ToggleGroup
              options={ANALYSIS_OPTIONS}
              selected={selectedAnalysis}
              onSelect={onSelectAnalysis}
            />
          </FilterSection>

          <FilterSection title={t('feed.sportType')}>
            <SportsGrid
              sportsOptions={sportsOptions}
              selectedSports={selectedSports}
              onSportToggle={handleSportToggle}
              loading={sportsNmarketLoading}
              error={sportsNmarketError}
            />
          </FilterSection>
        </FilterContent>

        <ButtonsContainer>
          <Reset onClick={handleReset}>
            {t('feed.resetAll')} {filterCount ? `(${filterCount})` : null}
          </Reset>
          <Results onClick={handleSubmit}>
            {t('feed.showResults')}
          </Results>
        </ButtonsContainer>
      </ModalContent>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  &.type-bottom-sheet {
    padding: 0px;
    z-index: 1000;
  }
  
  .modal-content {
    padding: 0px;
  }
`

const ModalContent = styled.div`
  position: relative;
  min-height: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary);
`

const TopModalContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  transition: color 0.2s;
  height: min-content;
  &:hover {
    color: #6b7280;
  }
`

const Title = styled.h2`
  font-size: var(--font-size-large);
  font-weight: 500;
  color: var(--text-primary);

`

const FilterContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 16px;
`

const Reset = styled.button`
  flex: 1;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border-radius: 8px;
  border: 1px solid var(--zinc-700);
  padding: 12px 16px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`

const Results = styled.button`
  flex: 1;
  background: var(--bg-quinary);
  color: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`
